import { nextTick } from 'vue';
import Cookies from 'js-cookie';

/**
 * Custom column resize directive – replaces the column-resizer library.
 *
 * Approach (inspired by PrimeVue DataTable):
 *  - Append a thin drag-handle element to every <th>.
 *  - On mousedown → track mousemove → update th.style.width on the fly.
 *  - On mouseup → persist only the dragged column's width to a cookie.
 *  - On mount → restore any previously persisted widths from the cookie.
 *  - Window/container resize is intentionally ignored: once a width is set
 *    by the user it stays fixed, period.
 *
 * Widths are kept by the column's key (data-column-key, which OrticTableHeader sets), so a hidden column does not
 * shift the others' widths; a header without a key falls back to its position. When the table's columns change
 * (a column shown or hidden), new headers get their handle and their width.
 */

const HANDLE_WIDTH = 6;           // px – clickable area for the drag handle
const COOKIE_EXPIRY = 365;        // days

function getCookieKey(tableId) {
  return `tableWidths_${tableId}`;
}

/** Read persisted widths: { "name": 200, "2": 150, … } */
function getManualWidths(tableId) {
  try {
    const raw = Cookies.get(getCookieKey(tableId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Migrate from old array format
    if (Array.isArray(parsed)) {
      Cookies.remove(getCookieKey(tableId));
      return {};
    }
    return parsed;
  } catch {
    return {};
  }
}

function saveManualWidths(tableId, widths) {
  Cookies.set(getCookieKey(tableId), JSON.stringify(widths), { expires: COOKIE_EXPIRY });
}

function widthKey(th, index) {
  return th.dataset.columnKey || String(index);
}

/** Apply saved widths to the <th> elements */
function applyManualWidths(el, tableId, migrate = false) {
  const widths = getManualWidths(tableId);
  const ths = el.querySelectorAll('thead th');
  // Widths saved before columns had keys were saved by position: move them to the keys, at mount (before any column
  // was hidden here, positions are what they were)
  let migrated = false;
  ths.forEach((th, index) => {
    if (!migrate) return;
    const key = th.dataset.columnKey;
    if (key && widths[String(index)] && !widths[key]) {
      widths[key] = widths[String(index)];
      delete widths[String(index)];
      migrated = true;
    }
  });
  if (migrated) saveManualWidths(tableId, widths);

  ths.forEach((th, index) => {
    const width = widths[widthKey(th, index)];
    if (!width) return;
    const maxW = th.dataset.maxWidth ? parseInt(th.dataset.maxWidth, 10) : 0;
    const minW = th.dataset.minWidth ? parseInt(th.dataset.minWidth, 10) : 0;
    let w = width;
    if (maxW > 0) w = Math.min(w, maxW);
    if (minW > 0) w = Math.max(w, minW);
    th.style.width = w + 'px';
    th.dataset.resized = '1';
  });
}

/** Forget the saved widths and let the table lay its columns out again ("Optimize columns", "Reset") */
export function clearManualWidths(el, tableId) {
  Cookies.remove(getCookieKey(tableId));
  el?.querySelectorAll('thead th[data-resized]').forEach(th => {
    th.style.width = '';
    delete th.dataset.resized;
  });
}

/** Create a drag handle element and append it to a <th> */
function createHandle(th) {
  const handle = document.createElement('div');
  handle.className = 'col-resize-handle';
  handle.style.cssText = `
    position: absolute;
    top: 0;
    right: 0;
    width: ${HANDLE_WIDTH}px;
    height: 100%;
    cursor: col-resize;
    z-index: 10;
    user-select: none;
  `;
  // Ensure the th is positioned for absolute children
  const pos = window.getComputedStyle(th).position;
  if (pos === 'static') {
    th.style.position = 'relative';
  }
  th.appendChild(handle);
  return handle;
}

/** Set up the drag handles on the headers that have none yet */
function initResizeHandles(el, tableId) {
  el.querySelectorAll('thead th').forEach((th) => {
    if (th.querySelector(':scope > .col-resize-handle')) return;
    const handle = createHandle(th);

    let startX = 0;
    let startWidth = 0;

    const getPageX = (e) => e.touches ? e.touches[0].pageX : e.pageX;

    const onDragMove = (e) => {
      const diff = getPageX(e) - startX;
      let newWidth = Math.max(30, startWidth + diff);  // min 30px
      const maxW = th.dataset.maxWidth ? parseInt(th.dataset.maxWidth, 10) : 0;
      const minW = th.dataset.minWidth ? parseInt(th.dataset.minWidth, 10) : 0;
      if (maxW > 0) newWidth = Math.min(newWidth, maxW);
      if (minW > 0) newWidth = Math.max(newWidth, minW);
      th.style.width = newWidth + 'px';
    };

    const onDragEnd = (e) => {
      document.removeEventListener('mousemove', onDragMove);
      document.removeEventListener('mouseup', onDragEnd);
      document.removeEventListener('touchmove', onDragMove);
      document.removeEventListener('touchend', onDragEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      // Persist this column's width, by its key (its position may change when other columns are hidden)
      const index = Array.from(el.querySelectorAll('thead th')).indexOf(th);
      const manual = getManualWidths(tableId);
      manual[widthKey(th, index)] = th.offsetWidth;
      saveManualWidths(tableId, manual);
      th.dataset.resized = '1';

      // Suppress the next click so releasing on a <th> doesn't trigger sorting
      document.addEventListener('click', (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
      }, { capture: true, once: true });
    };

    const onDragStart = (e) => {
      e.preventDefault();
      e.stopPropagation();
      startX = getPageX(e);
      startWidth = th.offsetWidth;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onDragMove);
      document.addEventListener('mouseup', onDragEnd);
      document.addEventListener('touchmove', onDragMove, { passive: false });
      document.addEventListener('touchend', onDragEnd);
    };

    handle.addEventListener('mousedown', onDragStart);
    handle.addEventListener('touchstart', onDragStart, { passive: false });

    // Prevent click from bubbling to th's sort handler
    handle.addEventListener('click', (e) => {
      e.stopPropagation();
    });
  });
}

export default {
  mounted(el, binding) {
    if (!binding.value) return;
    const tableId = binding.value;

    nextTick().then(() => {
      // Restore saved widths first
      applyManualWidths(el, tableId, true);
      // Set up drag handles
      initResizeHandles(el, tableId);
    });
  },
  // A column shown or hidden: the new headers get their width and their handle
  updated(el, binding) {
    if (!binding.value) return;
    applyManualWidths(el, binding.value);
    initResizeHandles(el, binding.value);
  }
};
